import { useRef, useEffect } from "react";

/**
 * Global request cancellation hook
 * Provides request cancellation functionality for API calls
 * @returns {Object} Request cancellation utilities
 */
export const useRequestCancellation = () => {
  const abortControllerRef = useRef(null);
  const requestIdRef = useRef(0);

  /**
   * Cancel the previous request if it exists
   */
  const cancelPreviousRequest = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  /**
   * Create a new request with cancellation support
   * @param {string} requestKey - Unique key for this request (optional)
   * @returns {Object} { signal, requestId }
   */
  const createNewRequest = (requestKey = null) => {
    cancelPreviousRequest();
    abortControllerRef.current = new AbortController();
    const currentRequestId = ++requestIdRef.current;

    if (requestKey) {
      // console.log(`🔥 Starting new request with ID: ${currentRequestId} for key: ${requestKey}`);
    } else {
      // console.log(`🔥 Starting new request with ID: ${currentRequestId}`);
    }

    return {
      signal: abortControllerRef.current.signal,
      requestId: currentRequestId
    };
  };

  /**
   * Check if this is the latest request
   * @param {number} requestId - Request ID to check
   * @returns {boolean} True if this is the latest request
   */
  const isLatestRequest = (requestId) => {
    return requestId === requestIdRef.current;
  };

  /**
   * Check if the current request was cancelled
   * @returns {boolean} True if request was cancelled
   */
  const isRequestCancelled = () => {
    return abortControllerRef.current?.signal.aborted || false;
  };

  /**
   * Get the current request ID
   * @returns {number} Current request ID
   */
  const getCurrentRequestId = () => {
    return requestIdRef.current;
  };

  /**
   * Cleanup function to cancel any pending requests
   */
  const cleanup = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      // console.log("🔥 Cleanup: All requests cancelled");
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Only cleanup if there's actually a pending request
      // This prevents StrictMode from cancelling initial requests
      if (abortControllerRef.current && !abortControllerRef.current.signal.aborted) {
        // Don't log during cleanup to avoid noise in StrictMode
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    createNewRequest,
    isLatestRequest,
    isRequestCancelled,
    getCurrentRequestId,
    cancelPreviousRequest,
    cleanup
  };
};
